/**
 * GlassButton — Apple-style frosted glass button. Same recipe as
 * web/src/components/GlassButton.tsx — translucent base + backdrop blur +
 * inset highlight.
 *
 * Three forms: Link (`to=`), anchor (`href=`), or plain button (default).
 * Disabled state baked in for the button form so loading states don't
 * need extra className gymnastics at call sites.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

type SizeKey = 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Record<SizeKey, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-11 px-5 text-sm',
};

type Variant = 'glass' | 'primary' | 'ghost' | 'danger';

const VARIANT_CLASSES: Record<Variant, string> = {
  glass: [
    'bg-white/[0.06] hover:bg-white/[0.12]',
    'backdrop-blur-xl backdrop-saturate-[1.8]',
    'border border-white/[0.12] hover:border-white/[0.22]',
    'shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_4px_24px_-8px_rgba(0,0,0,0.4)]',
    'text-slate-100',
  ].join(' '),
  primary: [
    'bg-gradient-to-r from-shark-500 via-shark-400 to-shark-500',
    'bg-[length:200%_100%] hover:bg-[position:100%_0]',
    'shadow-lg shadow-shark-500/30 hover:shadow-shark-500/40',
    'text-white border border-shark-400/40',
    'transition-[background-position] duration-700',
  ].join(' '),
  ghost: [
    'bg-transparent hover:bg-white/[0.05]',
    'border border-white/10 hover:border-white/10',
    'text-slate-200',
  ].join(' '),
  danger: [
    'bg-rose-900/30 hover:bg-rose-900/50',
    'border border-rose-700/60 hover:border-rose-600',
    'text-rose-200',
  ].join(' '),
};

const BASE = 'inline-flex items-center justify-center gap-1.5 rounded-full font-medium whitespace-nowrap active:scale-[0.98] transition-all duration-200';

interface CommonProps {
  children: ReactNode;
  size?: SizeKey;
  variant?: Variant;
  className?: string;
}

type LinkProps = CommonProps & { to: string; href?: never; onClick?: never };
type AnchorProps = CommonProps & { href: string; to?: never; target?: string; rel?: string };
type ButtonProps = CommonProps & {
  to?: never;
  href?: never;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
  type?: 'button' | 'submit';
  disabled?: boolean;
  ariaLabel?: string;
};

type GlassButtonProps = LinkProps | AnchorProps | ButtonProps;

export function GlassButton(props: GlassButtonProps): JSX.Element {
  const { children, size = 'md', variant = 'glass', className = '' } = props;
  const classes = `${BASE} ${SIZE_CLASSES[size]} ${VARIANT_CLASSES[variant]} ${className}`;

  if ('to' in props && props.to !== undefined) {
    return (
      <Link to={props.to} className={classes}>
        {children}
      </Link>
    );
  }
  if ('href' in props && props.href !== undefined) {
    return (
      <a
        href={props.href}
        className={classes}
        {...(props.target !== undefined ? { target: props.target } : {})}
        {...(props.rel !== undefined ? { rel: props.rel } : {})}
      >
        {children}
      </a>
    );
  }
  return (
    <button
      type={props.type ?? 'button'}
      onClick={props.onClick}
      disabled={props.disabled ?? false}
      aria-label={props.ariaLabel}
      className={`${classes} disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}
